import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Flame, MapPin, Navigation, Phone, HandHeart, LocateFixed, ShieldCheck, Globe } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PublicNav } from "@/components/PublicNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const RADIUS_MI = 150;
const CAT_COLORS = { partner: "#065F46", food: "#C2410C", shelter: "#7C3AED", healthcare: "#DC2626", clinic: "#2563EB", recovery: "#0D9488", community: "#57534E" };
const FILTERS = [
  { key: "all", label: "All" },
  { key: "food", label: "Food" },
  { key: "shelter", label: "Shelter" },
  { key: "healthcare", label: "Hospitals & ER" },
  { key: "clinic", label: "Clinics & Pharmacy" },
  { key: "recovery", label: "Recovery & Mental Health" },
  { key: "community", label: "Community & Faith" },
];

const pin = (color, warming) => L.divIcon({
  className: "",
  html: `<div style="background:${color};width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);color:white;font-size:12px">${warming ? "🔥" : "•"}</span></div>`,
  iconSize: [26, 26], iconAnchor: [13, 26],
});
const meIcon = L.divIcon({ className: "", html: `<div style="background:#0EA5E9;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 0 0 4px rgba(14,165,233,.3)"></div>`, iconSize: [18, 18], iconAnchor: [9, 9] });

export default function NearMe() {
  const mapRef = useRef(null);
  const mapEl = useRef(null);
  const markersRef = useRef(null);
  const routeRef = useRef(null);
  const [partners, setPartners] = useState([]);
  const [resources, setResources] = useState([]);
  const [pos, setPos] = useState(null);
  const [located, setLocated] = useState(false);
  const [loadingDir, setLoadingDir] = useState(false);
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [presets, setPresets] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);
  const [form, setForm] = useState({ item_key: "", pickup_org_id: "", requester_name: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [params] = useSearchParams();

  const fetchDirectory = useCallback(async (coords, cat) => {
    api.get("/orgs/public", { params: coords ? { lat: coords.lat, lng: coords.lng, radius_mi: RADIUS_MI } : {} })
      .then((r) => setPartners(r.data.filter((o) => o.latitude && o.longitude)));
    if (coords) {
      setLoadingDir(true);
      try {
        const { data } = await api.get("/aid/nearby", { params: { lat: coords.lat, lng: coords.lng, radius_mi: 25, category: cat } });
        setResources(data.places || []);
        if (data.error) toast.info("Live directory is busy — showing what we could load.");
      } catch {
        setResources([]);
      } finally {
        setLoadingDir(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchDirectory(null, "all");
    api.get("/presets").then((r) => setPresets(r.data));
    if (params.get("request") === "1") setReqOpen(true);
  }, [fetchDirectory, params]);

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { zoomControl: true }).setView([39.5, -98.35], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(map);
    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    locate();
    // eslint-disable-next-line
  }, []);

  const locate = () => {
    if (!navigator.geolocation) { toast.info("Location isn't available on this device."); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const c = { lat: p.coords.latitude, lng: p.coords.longitude };
        setPos(c);
        setLocated(true);
        fetchDirectory(c, category);
        if (mapRef.current) mapRef.current.setView([c.lat, c.lng], 12);
      },
      () => toast.info("Enable location to see aid resources nearest to you."),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const changeCategory = (cat) => {
    setCategory(cat);
    if (pos) fetchDirectory(pos, cat);
    else toast.info("Turn on your location to load resources near you.");
  };

  const combined = [
    ...partners.map((o) => ({
      id: o.id, name: o.org_name, category: "partner", category_label: "Verified partner",
      latitude: o.latitude, longitude: o.longitude, address: o.address || o.city, phone: o.phone,
      is_warming_shelter: o.is_warming_shelter, services: o.services, distance_mi: o.distance_mi, _partner: true,
    })),
    ...resources,
  ];

  useEffect(() => {
    const map = mapRef.current, grp = markersRef.current;
    if (!map || !grp) return;
    grp.clearLayers();
    if (pos) L.marker([pos.lat, pos.lng], { icon: meIcon }).addTo(grp).bindPopup("You are here");
    combined.forEach((o) => {
      const m = L.marker([o.latitude, o.longitude], { icon: pin(CAT_COLORS[o.category] || "#57534E", o.is_warming_shelter) });
      m.addTo(grp).on("click", () => setSelected(o));
      m.bindPopup(`<b>${o.name}</b><br/>${o.category_label}${o.address ? "<br/>" + o.address : ""}`);
    });
    // eslint-disable-next-line
  }, [partners, resources, pos]);

  const drawRoute = useCallback(async (place) => {
    const map = mapRef.current;
    if (!map || !pos) { toast.info("Enable your location first to get directions."); return; }
    if (routeRef.current) { map.removeLayer(routeRef.current); routeRef.current = null; }
    try {
      const url = `https://router.project-osrm.org/route/v1/foot/${pos.lng},${pos.lat};${place.longitude},${place.latitude}?overview=full&geometries=geojson`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.routes && data.routes[0]) {
        const coords = data.routes[0].geometry.coordinates.map((c) => [c[1], c[0]]);
        routeRef.current = L.polyline(coords, { color: "#065F46", weight: 5, opacity: 0.8 }).addTo(map);
        map.fitBounds(routeRef.current.getBounds(), { padding: [50, 50] });
        setRouteInfo({ id: place.id, miles: (data.routes[0].distance / 1609.34).toFixed(1), mins: Math.round(data.routes[0].duration / 60) });
      }
    } catch { toast.error("Could not load the route."); }
  }, [pos]);

  const submitRequest = async () => {
    if (!form.item_key || !form.pickup_org_id) { toast.error("Pick an item and a pickup location."); return; }
    if (!form.requester_name.trim()) { toast.error("Please enter a name or nickname."); return; }
    setSubmitting(true);
    try {
      await api.post("/requests", form);
      toast.success("Your request is submitted. The organization will review it, then a donor can fund it.");
      setReqOpen(false);
      setForm({ item_key: "", pickup_org_id: "", requester_name: "", note: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const list = combined.slice().sort((a, b) => (a.distance_mi ?? 9e9) - (b.distance_mi ?? 9e9));

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <PublicNav />
      <div className="pt-16 flex-1 grid lg:grid-cols-[400px_1fr]">
        {/* List */}
        <aside className="order-2 lg:order-1 max-h-[calc(100vh-4rem)] overflow-y-auto border-r border-stone-200 bg-white">
          <div className="p-6 border-b border-stone-100">
            <h1 className="font-heading text-2xl font-semibold text-stone-900">Aid near you</h1>
            <p className="text-sm text-stone-500 mt-1">Real shelters, food, healthcare and support around you.</p>
            <div className="flex gap-2 mt-3">
              <Button data-testid="locate-me" onClick={locate} variant="outline" className="rounded-full flex-1"><LocateFixed className="w-4 h-4 mr-2" />Use my location</Button>
              <Button data-testid="open-request" onClick={() => setReqOpen(true)} className="rounded-full bg-orange-700 hover:bg-orange-800 text-white flex-1"><HandHeart className="w-4 h-4 mr-2" />Request help</Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-4" data-testid="category-filters">
              {FILTERS.map((f) => (
                <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => changeCategory(f.key)}
                  className={`text-xs font-medium rounded-full px-3 py-1.5 transition-colors ${category === f.key ? "bg-emerald-800 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div data-testid="aid-list">
            {loadingDir && <p className="p-6 text-sm text-stone-500">Loading… this may take a minute while we gather live resources near you.</p>}
            {!loadingDir && list.length === 0 && (
              <div className="p-6 text-center" data-testid="no-aid-message">
                <MapPin className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                <p className="text-stone-700 font-medium">{located ? "No aid resources found near you yet" : "Turn on your location"}</p>
                <p className="text-sm text-stone-500 mt-1">{located ? "Try a different category or a wider area — new resources appear as we expand coverage." : "Tap \"Use my location\" to load real shelters, food, and healthcare near you."}</p>
              </div>
            )}
            {!loadingDir && list.map((o) => (
              <div key={o.id} className={`p-5 border-b border-stone-100 cursor-pointer transition-colors ${selected?.id === o.id ? "bg-emerald-50" : "hover:bg-stone-50"}`} onClick={() => { setSelected(o); mapRef.current?.setView([o.latitude, o.longitude], 15); }}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-stone-900 flex items-center gap-1.5">
                    {o.name}
                    {o._partner && <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" title="Verified partner" />}
                  </p>
                  {o.is_warming_shelter && <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 rounded-full shrink-0"><Flame className="w-3 h-3 mr-1" />Warming</Badge>}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs font-medium rounded-full px-2 py-0.5" style={{ background: (CAT_COLORS[o.category] || "#57534E") + "1A", color: CAT_COLORS[o.category] || "#57534E" }}>{o.category_label}</span>
                  {typeof o.distance_mi === "number" && <span className="text-xs text-stone-500">{o.distance_mi} mi away</span>}
                </div>
                {o.address && <p className="flex items-center gap-1 text-sm text-stone-500 mt-1"><MapPin className="w-3.5 h-3.5" /> {o.address}</p>}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <Button data-testid={`directions-${o.id}`} onClick={(e) => { e.stopPropagation(); drawRoute(o); }} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white h-9 text-sm"><Navigation className="w-3.5 h-3.5 mr-1.5" />Directions</Button>
                  <a data-testid={`gmaps-${o.id}`} href={`https://www.google.com/maps/dir/?api=1&destination=${o.latitude},${o.longitude}`} target="_blank" rel="noreferrer" className="text-sm text-stone-500 hover:text-emerald-800 underline">Open maps</a>
                  {o.phone && <a href={`tel:${o.phone}`} className="text-stone-400 hover:text-emerald-800"><Phone className="w-4 h-4" /></a>}
                  {o.website && <a href={o.website} target="_blank" rel="noreferrer" className="text-stone-400 hover:text-emerald-800"><Globe className="w-4 h-4" /></a>}
                </div>
                {routeInfo?.id === o.id && <p className="text-xs text-emerald-800 font-medium mt-2">≈ {routeInfo.miles} mi · {routeInfo.mins} min</p>}
              </div>
            ))}
          </div>
        </aside>

        {/* Map */}
        <div className="order-1 lg:order-2 relative">
          <div ref={mapEl} data-testid="aid-map" className="w-full h-[45vh] lg:h-[calc(100vh-4rem)]" />
        </div>
      </div>

      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="rounded-2xl z-[1200]">
          <DialogHeader><DialogTitle className="font-heading">Request help</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-600">It's free and no login is needed. Choose what you need and where you'll pick it up. A verified partner organization reviews it, then a donor funds it.</p>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-stone-500">What do you need?</Label>
              <Select value={form.item_key} onValueChange={(v) => setForm((f) => ({ ...f, item_key: v }))}>
                <SelectTrigger data-testid="req-item" className="rounded-xl h-11"><SelectValue placeholder="Select an item" /></SelectTrigger>
                <SelectContent className="z-[1300]">
                  {presets.map((p) => <SelectItem key={p.key} value={p.key}>{p.name} — ${p.price.toFixed(2)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-stone-500">Pickup location (verified partner)</Label>
              <Select value={form.pickup_org_id} onValueChange={(v) => setForm((f) => ({ ...f, pickup_org_id: v }))}>
                <SelectTrigger data-testid="req-org" className="rounded-xl h-11"><SelectValue placeholder="Choose where to pick it up" /></SelectTrigger>
                <SelectContent className="z-[1300]">
                  {partners.map((o) => <SelectItem key={o.id} value={o.id}>{o.org_name} — {o.city}</SelectItem>)}
                </SelectContent>
              </Select>
              {partners.length === 0 && <p className="text-xs text-orange-600">No verified partner organizations in your area yet — check back soon.</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-stone-500">Name or nickname <span className="text-orange-600">*</span></Label>
              <Input data-testid="req-name" required value={form.requester_name} onChange={(e) => setForm((f) => ({ ...f, requester_name: e.target.value }))} placeholder="So the org knows who to give it to" className="rounded-xl h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-stone-500">Note (optional)</Label>
              <Textarea data-testid="req-note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Size, color, anything helpful" className="rounded-xl" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="req-submit" onClick={submitRequest} disabled={submitting} className="w-full rounded-full bg-orange-700 hover:bg-orange-800 text-white h-11">
              {submitting ? "Posting…" : "Post my request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
